import React, { useEffect, useState } from 'react'
import { Snackbar } from '@material-ui/core'
import { Alert, Color } from '@material-ui/lab'
import { connect } from 'react-redux'
import { IGlobalState } from '../../../../Root/store/appReducer'
import { getNotification } from '../../../../common/notifications/NotificationSelectors'
import { clearNotification } from '../../../../common/notifications/NotificationActions'
import { INotification } from '../../../../common/notifications/NotificationReducers'
import { TNotificationType } from '../../../../components/Notification/Notification'

interface IStateProps {
  notification: INotification | null
}

interface IDispatchProps {
  clearNotification: typeof clearNotification
}

type IChatSnackbarProps = IStateProps & IDispatchProps

// In this app a 'warning' notification is the "error" style (rendered red), so map it to the
// red MUI Alert severity. 'success' / 'info' map directly.
const severityMap: Record<TNotificationType, Color> = {
  warning: 'error',
  success: 'success',
  info: 'info',
}

/**
 * Renders the global notification as a floating snackbar, scoped to the chat view.
 * The chat view intentionally does not mount the inline NotificationsWrapper (it would shift
 * the layout), so this overlay-based snackbar surfaces export feedback / errors instead.
 */
const ChatSnackbar = ({ notification, clearNotification }: IChatSnackbarProps) => {
  // Keep the last notification while the snackbar animates closed.
  const [current, setCurrent] = useState<INotification | null>(null)

  useEffect(() => {
    if (notification) {
      setCurrent(notification)
    }
  }, [notification])

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    clearNotification()
  }

  return (
    <Snackbar
      open={!!notification}
      autoHideDuration={6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      style={{ zIndex: 9999 }}
    >
      <Alert
        onClose={handleClose}
        severity={current ? severityMap[current.type] : 'info'}
        elevation={6}
        variant="filled"
      >
        {current?.content}
      </Alert>
    </Snackbar>
  )
}

const mapStateToProps = (state: IGlobalState): IStateProps => ({
  notification: getNotification(state),
})

const mapDispatchToProps = { clearNotification }

export default connect(mapStateToProps, mapDispatchToProps)(ChatSnackbar)
